import { useEffect, useRef, useState } from "react";
import { ImagemAmpliavel } from "../../components/Ampliar.js";
import { Botao, Girando, Vazio } from "../../components/ui.js";
import { ChaveDeVersaoDaFoto } from "../impressao/FiltrosDasFotos.js";
import { VisorDaFoto } from "../impressao/VisorDaFoto.js";
import { imagensNaVersao, rotuloDaLupa, versaoEmUso } from "../impressao/ampliar-fotos.js";
import type { FotosPublicadas } from "../impressao/useFotosPublicadas.js";
import { EscolherFoto } from "./EscolherFoto.js";

/**
 * O bloco "Foto" do formulário: a foto escolhida em tamanho grande, na proporção dela, para conferir
 * que é a certa antes de gastar uma folha do pacote. Tocar nela abre o visor com zoom; "Trocar foto"
 * abre a tela de escolha.
 *
 * Sem foto, o bloco convida a escolher. A lista com filtros e a grade moram na tela de escolha, e não
 * aqui: uma grade dentro de uma coluna de 26 rem tinha miniaturas de 70 px.
 */
export function BlocoDaFoto({
  bandeja,
  fotoId,
  aoEscolher,
}: {
  bandeja: FotosPublicadas;
  fotoId: number | null;
  aoEscolher: (id: number) => void;
}) {
  const [escolhendo, setEscolhendo] = useState(false);
  const [ampliando, setAmpliando] = useState(false);
  const foto = fotoId !== null ? bandeja.vistas.get(fotoId) : undefined;

  // Depois de escolher, o foco vai para "Trocar foto": o botão que abriu a escolha ("Escolher a
  // foto") deixa de existir quando a foto aparece, e o foco ficaria perdido no fim da página.
  const trocar = useRef<HTMLButtonElement>(null);
  const [devolverFoco, setDevolverFoco] = useState(false);
  useEffect(() => {
    if (!devolverFoco || !trocar.current) return;
    trocar.current.focus();
    setDevolverFoco(false);
  }, [devolverFoco, foto]);

  const versao = foto ? versaoEmUso(foto, bandeja.versao) : "peca";

  return (
    <div>
      <div className="mb-2 flex min-h-11 items-center justify-between gap-3">
        <h2 className="text-[0.9375rem] font-medium text-texto">Foto</h2>
        {foto && (
          <Botao ref={trocar} tom="quieto" className="min-h-11" onClick={() => setEscolhendo(true)}>
            Trocar foto
          </Botao>
        )}
      </div>

      {foto ? (
        <>
          <div className="grid place-items-center rounded-peca bg-superficie p-2">
            <ImagemAmpliavel rotulo={rotuloDaLupa(foto.nome)} aoAmpliar={() => setAmpliando(true)}>
              <img
                src={imagensNaVersao(foto, versao).tela}
                alt={`Foto de ${foto.nome}`}
                className="block max-h-[22rem] w-auto max-w-full rounded-peca object-contain"
              />
            </ImagemAmpliavel>
          </div>
          <p className="mt-2 text-[0.9375rem] font-medium text-texto">{foto.nome}</p>
          <div className="mt-2">
            <ChaveDeVersaoDaFoto
              bandeja={bandeja}
              aparecer={foto.foto !== undefined}
              legendaPeca="O retrato da peça publicada, sem a capa da vivência, vai de referência à IA."
              legendaFoto="A imagem que a IA gerou, inteira e sem capa, vai de referência."
            />
          </div>
        </>
      ) : fotoId !== null && bandeja.consulta.isLoading ? (
        <Girando className="mx-auto my-10 size-5 text-texto-fraco" />
      ) : (
        <div className="rounded-peca border border-dashed border-borda-forte">
          <Vazio
            compacto
            titulo="Nenhuma foto escolhida"
            descricao="Escolha a foto publicada que a IA vai transformar nos marcadores."
            acao={
              <Botao tom="brasa" className="min-h-11" onClick={() => setEscolhendo(true)}>
                Escolher a foto
              </Botao>
            }
          />
        </div>
      )}

      {escolhendo && (
        <EscolherFoto
          bandeja={bandeja}
          escolhida={fotoId}
          aoEscolher={(id) => {
            aoEscolher(id);
            setEscolhendo(false);
            setDevolverFoco(true);
          }}
          aoFechar={() => setEscolhendo(false)}
        />
      )}

      {ampliando && foto && (
        <VisorDaFoto
          foto={foto}
          versao={bandeja.versao}
          // Duas versões: depois de compará-las, escolhe-se uma. Com uma só, não há o que escolher.
          acao={
            foto.foto
              ? { rotulo: "Usar esta versão", aoAcionar: (v) => bandeja.escolherVersao(v) }
              : undefined
          }
          aoFechar={() => setAmpliando(false)}
        />
      )}
    </div>
  );
}
